// dNews / Blog
//
// Two subviews via a top toggle:
//   1. Compose       - post list (left rail) + block editor (main)
//   2. Manage Categories - grid of categories with reorder + edit + delete
//
// Block editor: ordered list of typed blocks (heading / paragraph /
// image / video / divider). Image + video blocks have left/full/right
// alignment toggles. Blocks are saved as a JSON array on the post.
//
// Media uploads use POST /api/dolluzcorp/blog/posts/upload-media which returns
// a URL the block stores in its `src` field. Media is served from
// /api/dolluzcorp/blog/posts/media/<filename> (no auth - admin marketing content).
//
// Publish lifecycle: draft → published (now) | draft → scheduled (later).
// A background worker (src/utils/dNews_Scheduler.js) flips scheduled posts
// to published when their time arrives.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import { dnAlert } from "./utils/dNews_Alert";
import {
    FaNewspaper, FaPlus, FaTrashAlt, FaPenSquare, FaImage, FaVideo, FaHeading, FaEye,
    FaParagraph, FaMinus, FaArrowUp, FaArrowDown, FaUpload, FaGlobe, FaClock,
    FaFileAlt, FaTimes, FaSave, FaTag, FaCheckCircle, FaColumns,
} from "react-icons/fa";
import { apiFetch, API_BASE } from "./utils/api";
import SectionVisibility from "./utils/Dolluzcorp_SectionVisibility";
import { RenderBlock, mediaUrl, mediaWidthPct, mediaSizeStyle } from "./utils/Dolluzcorp_Blog_render";
import Select from "react-select";
import { checkboxMultiProps } from "./utils/MultiSelectCheckbox";
import DNewsSelect from "./utils/dNews_Select";
import logo_eagle from "./assets/img/logo_eagle.png";
import "./dSpr.css";
import "./dNews_Newsletter.css";
import "./Dolluzcorp_Blog_config.css";

// Lower bound of the media size slider. The renderer clamps to the same
// floor when it reads widthPct, so the two agree without importing across.
const MEDIA_PCT_MIN = 10;

// react-select renders its menu inline, so a card with overflow:hidden clips
// it. Portal the menu to <body>.
// Same badge wording as Updates / Policies, so one glance reads the same
// wherever the author is.
const audienceBadge = (aType, aVal) => {
    if (!aType || aType === "general") return "Everyone";
    if (aType === "all_employees") return "All Employees";
    const count = (aVal && aVal !== "ALL") ? String(aVal).split(",").filter(Boolean).length : 0;
    const label = { department: "Department", job_position: "Job Position", location: "Location", access_level: "Access Level", employee: "Employee" }[aType] || aType;
    return `${label}${count ? ` (${count})` : ""}`;
};

const SELECT_PORTAL = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed",
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
};

// Audience - identical set and wording to Updates / Policies / Banner, so the
// same choice means the same thing wherever an author sets it.
const AUDIENCE_TYPES = [
    { value: "general", label: "General - Everyone (public)" },
    { value: "all_employees", label: "All Employees (logged-in staff)" },
    { value: "department", label: "Department" },
    { value: "job_position", label: "Job Position" },
    { value: "location", label: "Location" },
    { value: "access_level", label: "Access Level" },
    { value: "employee", label: "Employee" },
];
const SCOPED = ["department", "job_position", "location", "access_level", "employee"];
const LOCATIONS = ["Chennai", "Hyderabad", "Pune", "Nagpur", "Mumbai"];
const ACCESS_LEVELS = ["Admin", "Sub Admin", "Manager", "User"];

const STATUS_PILL = {
    draft: { className: "dnews-blog-pill draft", label: "Draft" },
    scheduled: { className: "dnews-blog-pill scheduled", label: "Scheduled" },
    published: { className: "dnews-blog-pill published", label: "Published" },
};

const BLOCK_TYPES = [
    { id: "heading", label: "Heading", icon: FaHeading },
    { id: "text", label: "Paragraph", icon: FaParagraph },
    { id: "image", label: "Image", icon: FaImage },
    { id: "video", label: "Video", icon: FaVideo },
    { id: "divider", label: "Divider", icon: FaMinus },
    { id: "row", label: "Row (2 cols)", icon: FaColumns },
];

// Leaf-only types - what's allowed inside a Row column. No nested rows,
// no dividers in cells (they'd just be a horizontal rule taking half the row).
const LEAF_TYPES = [
    { id: "heading", label: "Heading", icon: FaHeading },
    { id: "text", label: "Paragraph", icon: FaParagraph },
    { id: "image", label: "Image", icon: FaImage },
    { id: "video", label: "Video", icon: FaVideo },
];

// Tiny unique id for new blocks - timestamp + counter is plenty since
// blocks live only inside a single post's blocks array.
let blockIdSeed = 1;
function newBlockId() { return `b${Date.now()}-${blockIdSeed++}`; }

export default function Dolluzcorp_Blog_config({ navSize }) {
    const navigate = useNavigate();
    const [subview, setSubview] = useState("compose");

    return (
        <div className="dspr-container">
            <div className={`dspr-dashboard ${navSize}`}>
                <div className="dspr-header-wrapper">
                    <h2>
                        <a className="navbar-brand" href="#"><img src={logo_eagle} alt="dAdmin Logo" style={{ height: 50, objectFit: "contain", marginLeft: -20 }} /></a>
                        dNews - Blog
                    </h2>
                    <p className="dspr-subtitle">Compose blog posts with rich content blocks. Publish now, schedule, or save as draft. Categories drive the (future) public site grouping.</p>
                </div>

                <div className="dspr-dashboard-content">
                    <div className="dnews-blog-subtabs">
                        {[
                            { id: "compose", label: "Compose", icon: FaPenSquare },
                            { id: "categories", label: "Manage Categories", icon: FaTag },
                        ].map(t => (
                            <button
                                key={t.id}
                                className={`dnews-blog-subtab ${subview === t.id ? "active" : ""}`}
                                onClick={() => setSubview(t.id)}
                            >
                                <t.icon /> {t.label}
                            </button>
                        ))}
                    </div>

                    <SectionVisibility section="blog" label="the Blog" />

                    {/* setSubview is passed so ComposeView can bump the user
                        to Manage Categories when no categories exist (you
                        can't compose a post without picking a category). */}
                    {subview === "compose" && <ComposeView navigate={navigate} setSubview={setSubview} />}
                    {subview === "categories" && <CategoriesView navigate={navigate} />}
                </div>
            </div>
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPOSE VIEW
// ═══════════════════════════════════════════════════════════════════════════
function ComposeView({ navigate, setSubview }) {
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedId, setSelectedId] = useState(null);  // null | "new" | numeric
    const [editor, setEditor] = useState(null);          // { title, category_id, blocks, status, ... }
    const [scheduleAt, setScheduleAt] = useState("");
    const [preview, setPreview] = useState(null);        // post being read, null = closed
    const [previewLoading, setPreviewLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [departments, setDepartments] = useState([]);
    const [jobPositions, setJobPositions] = useState([]);
    const [employees, setEmployees] = useState([]);

    const fetchAll = useCallback(async () => {
        setLoadingList(true);
        try {
            const [pRes, cRes] = await Promise.all([
                apiFetch("/api/dolluzcorp/blog/posts/list", { credentials: "include" }),
                apiFetch("/api/dolluzcorp/blog/categories/list", { credentials: "include" }),
            ]);
            if (pRes.status === 401 || pRes.status === 403) { navigate("/login"); return; }
            setPosts(await pRes.json());
            setCategories(await cRes.json());
        } finally { setLoadingList(false); }
    }, [navigate]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    // Audience pickers need the same lookups the other Inside D pages use.
    useEffect(() => {
        (async () => {
            try { const r = await apiFetch("/api/Dolluzcorp/departments/list"); const d = await r.json(); if (d.success) setDepartments(d.data); } catch { /* ignore */ }
            try { const r = await apiFetch("/api/Employee_configuration/get-job_position_config"); setJobPositions(await r.json()); } catch { /* ignore */ }
            try { const r = await apiFetch("/api/employee/directory"); setEmployees(await r.json()); } catch { /* ignore */ }
        })();
    }, []);

    const openNew = () => {
        // Hard-bounce to Manage Categories - posting without a category is
        // impossible (FK), and forcing the admin through the categories tab
        // is friendlier than a dismissible toast they can ignore.
        if (categories.length === 0) {
            dnAlert.warning("No categories yet", "Create your first category, then compose a post.");
            if (setSubview) setSubview("categories");
            return;
        }
        setSelectedId("new");
        setEditor({
            title: "",
            category_id: categories[0].id,
            blocks: [],
            audience_type: "general",
            audience_value: [],
            status: "draft",
        });
        setScheduleAt("");
    };

    // Clicking a card opens a READ view, not the editor - the same thing a
    // visitor sees. Editing is a deliberate second step from inside it.
    const audienceOptionsFor = (type) => {
        switch (type) {
            case "department": return departments.map(d => ({ value: String(d.department_id), label: d.department_name }));
            case "job_position": return jobPositions.map(j => ({ value: String(j.job_id), label: j.job_name }));
            case "location": return LOCATIONS.map(l => ({ value: l, label: l }));
            case "access_level": return ACCESS_LEVELS.map(l => ({ value: l, label: l }));
            case "employee": return employees.map(e => ({ value: String(e.emp_id), label: `${e.emp_first_name} ${e.emp_last_name}` }));
            default: return [];
        }
    };

    // Delete from the card grid. Separate from the composer's Delete, which
    // only ever acts on the post currently open.
    const deletePost = async (post) => {
        const ok = await dnAlert.confirm({
            title: `Delete "${post.title}"?`,
            message: "The post is removed from the portal. Uploaded files stay on disk.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await apiFetch(`/api/dolluzcorp/blog/posts/${post.id}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) { dnAlert.error("Couldn’t delete", "Something went wrong. Please try again."); return; }
        dnAlert.success("Deleted", "Post removed");
        // Clear the composer if it was showing the post just deleted.
        if (selectedId === post.id) { setSelectedId(null); setEditor(null); }
        if (preview?.id === post.id) setPreview(null);
        await fetchAll();
    };

    const openPreview = async (id) => {
        setPreviewLoading(true);
        setPreview({ id });
        try {
            const res = await apiFetch(`/api/dolluzcorp/blog/posts/${id}`, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to load");
            setPreview(await res.json());
        } catch (e) {
            setPreview(null);
            dnAlert.error("Couldn’t open preview", e.message);
        } finally {
            setPreviewLoading(false);
        }
    };

    // Jump from the preview straight into editing the same post.
    const editFromPreview = async () => {
        const id = preview?.id;
        setPreview(null);
        if (id) await openPost(id);
    };

    const openPost = async (id) => {
        try {
            const res = await apiFetch(`/api/dolluzcorp/blog/posts/${id}`, { credentials: "include" });
            if (!res.ok) throw new Error("Failed to load");
            const p = await res.json();
            setSelectedId(p.id);
            setEditor({
                title: p.title,
                category_id: p.category_id,
                blocks: Array.isArray(p.blocks) ? p.blocks : [],
                audience_type: p.audience_type || "general",
                audience_value: (p.audience_value && p.audience_value !== "ALL") ? String(p.audience_value).split(",").filter(Boolean) : [],
                status: p.status,
            });
            setScheduleAt(p.scheduled_at ? p.scheduled_at.slice(0, 16) : "");
        } catch (e) {
            dnAlert.error("Load failed", e.message);
        }
    };

    // ── Block manipulation helpers ───────────────────────────────────────
    const updateBlock = (id, patch) => {
        setEditor(e => ({ ...e, blocks: e.blocks.map(b => b.id === id ? { ...b, ...patch } : b) }));
    };
    const addBlock = (type) => {
        const base = { id: newBlockId(), type };
        if (type === "heading" || type === "text") base.html = "";
        if (type === "image" || type === "video") { base.src = ""; base.align = "full"; base.widthPct = 100; }
        if (type === "image") base.alt = "";
        // Row: two empty slots. Each slot is null until the admin picks a
        // block type for it. Slots can be filled / cleared independently.
        if (type === "row") base.cols = [null, null];
        setEditor(e => ({ ...e, blocks: [...e.blocks, base] }));
    };
    const removeBlock = (id) => setEditor(e => ({ ...e, blocks: e.blocks.filter(b => b.id !== id) }));
    const moveBlock = (id, dir) => {
        setEditor(e => {
            const idx = e.blocks.findIndex(b => b.id === id);
            if (idx < 0) return e;
            const next = [...e.blocks];
            const swap = dir === "up" ? idx - 1 : idx + 1;
            if (swap < 0 || swap >= next.length) return e;
            [next[idx], next[swap]] = [next[swap], next[idx]];
            return { ...e, blocks: next };
        });
    };

    // ── Save (draft) - PUT for existing, POST for new ────────────────────
    //
    // Before the network save, walk the blocks and upload every pending
    // File (one that was picked but not yet sent). Swap the blob: URL for
    // the returned server URL, strip the _pendingFile (it must NOT go to
    // the backend JSON). This is the mechanism that keeps the server free
    // of orphan files: nothing hits disk until the admin explicitly saves.
    const uploadPendingMedia = async (blocks) => {
        // Walk the tree (including row.cols), upload any _pendingFile
        // found, mutate the block in place with the new src.
        const visit = async (b) => {
            if (!b) return;
            if (b._pendingFile instanceof File) {
                const fd = new FormData();
                fd.append("file", b._pendingFile);
                const res = await apiFetch("/api/dolluzcorp/blog/posts/upload-media", {
                    method: "POST", credentials: "include", body: fd,
                });
                let data;
                try { data = await res.json(); }
                catch { throw new Error(res.status === 413 ? "File too large (5MB max)" : `Server error (${res.status})`); }
                if (!res.ok) throw new Error(data.error || `Server error (${res.status})`);
                // Revoke the blob URL we created for the preview to free
                // browser memory. Then point the block at the real server URL.
                if (b.src && typeof b.src === "string" && b.src.startsWith("blob:")) {
                    URL.revokeObjectURL(b.src);
                }
                b.src = data.url;
                delete b._pendingFile;
            }
            if (b.type === "row" && Array.isArray(b.cols)) {
                for (const c of b.cols) await visit(c);
            }
        };
        // Deep-clone so we don't mutate React state directly; then return
        // the cleaned copy for the PUT/POST payload.
        const copy = JSON.parse(JSON.stringify(blocks, (k, v) => {
            // File objects don't survive JSON.stringify - preserve them
            // via a sentinel that we'll re-pair after parse.
            if (v instanceof File) return { __file__: true };
            return v;
        }));
        // Re-pair File objects: walk both trees in parallel, copy File refs.
        const repair = (src, dst) => {
            if (!src || !dst) return;
            if (src._pendingFile instanceof File) dst._pendingFile = src._pendingFile;
            if (src.type === "row" && Array.isArray(src.cols)) {
                for (let i = 0; i < src.cols.length; i++) repair(src.cols[i], dst.cols[i]);
            }
        };
        for (let i = 0; i < blocks.length; i++) repair(blocks[i], copy[i]);
        // Now upload pending files in the copy.
        for (const b of copy) await visit(b);
        return copy;
    };

    // Leave the composer without reloading: drop the draft, deselect the post,
    // and revoke any blob previews so pending uploads do not leak memory.
    const closeEditor = () => {
        (editor?.blocks || []).forEach(function revoke(b) {
            if (b?.src && typeof b.src === "string" && b.src.startsWith("blob:")) URL.revokeObjectURL(b.src);
            if (b?.type === "row" && Array.isArray(b.cols)) b.cols.forEach(c => c && revoke(c));
        });
        setEditor(null);
        setSelectedId(null);
        setScheduleAt("");
    };

    const saveBase = async () => {
        if (!editor.title.trim()) { dnAlert.warning("Title is required"); return null; }
        if (!editor.category_id) { dnAlert.warning("Category is required"); return null; }
        if (SCOPED.includes(editor.audience_type) && !(editor.audience_value || []).length) {
            dnAlert.warning("Check the form", "Pick at least one value for the selected audience.");
            return null;
        }

        let blocksToSave;
        try {
            blocksToSave = await uploadPendingMedia(editor.blocks);
        } catch (e) {
            dnAlert.error("Media upload failed", e.message);
            return null;
        }

        // Mirror the swapped src URLs back into editor state so the next
        // save (no file change) doesn't try to re-upload anything. Doing
        // this AFTER a successful upload keeps the preview alive.
        setEditor(prev => prev ? { ...prev, blocks: blocksToSave } : prev);

        const payload = {
            title: editor.title.trim(),
            category_id: editor.category_id,
            audience_type: editor.audience_type || "general",
            audience_value: SCOPED.includes(editor.audience_type) ? (editor.audience_value || []).join(",") : "",
            blocks: blocksToSave,
        };
        const isNew = selectedId === "new";
        const url = isNew ? "/api/dolluzcorp/blog/posts/create" : `/api/dolluzcorp/blog/posts/${selectedId}`;
        const res = await apiFetch(url, {
            method: isNew ? "POST" : "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) { dnAlert.error("Save failed", data.error); return null; }
        const id = isNew ? data.id : selectedId;
        if (isNew) setSelectedId(id);
        return id;
    };

    const onSaveDraft = async () => {
        setBusy(true);
        try {
            const id = await saveBase();
            if (!id) return;
            // saveBase doesn't change status. If the row was previously
            // published or scheduled, this is just a content update - no
            // status flip. To revert explicitly use the Publish→Draft button.
            await fetchAll();
            dnAlert.success("Saved");
        } finally { setBusy(false); }
    };

    const onPublishNow = async () => {
        setBusy(true);
        try {
            const id = await saveBase();
            if (!id) return;
            const res = await apiFetch(`/api/dolluzcorp/blog/posts/${id}/publish`, { method: "POST", credentials: "include" });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setEditor(e => ({ ...e, status: "published" }));
            await fetchAll();
            dnAlert.success("Published");
        } catch (e) {
            dnAlert.error("Publish failed", e.message);
        } finally { setBusy(false); }
    };

    const onSchedule = async () => {
        if (!scheduleAt) { dnAlert.warning("Pick a date/time"); return; }
        if (new Date(scheduleAt).getTime() < Date.now()) {
            dnAlert.warning("Scheduled time must be in the future"); return;
        }
        setBusy(true);
        try {
            const id = await saveBase();
            if (!id) return;
            const res = await apiFetch(`/api/dolluzcorp/blog/posts/${id}/schedule`, {
                method: "POST", credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ scheduled_at: new Date(scheduleAt).toISOString() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setEditor(e => ({ ...e, status: "scheduled" }));
            await fetchAll();
            dnAlert.success("Scheduled", `Will publish on ${new Date(data.scheduled_for).toLocaleString()}`);
        } catch (e) {
            dnAlert.error("Schedule failed", e.message);
        } finally { setBusy(false); }
    };

    const onRevertToDraft = async () => {
        const res = await apiFetch(`/api/dolluzcorp/blog/posts/${selectedId}/draft`, { method: "POST", credentials: "include" });
        if (!res.ok) return;
        setEditor(e => ({ ...e, status: "draft" }));
        await fetchAll();
    };

    const onDelete = async () => {
        if (!selectedId || selectedId === "new") return;
        const ok = await dnAlert.confirm({
            title: `Delete "${editor.title}"?`,
            message: "This post and its uploaded media references will be removed. Uploaded files stay on disk.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await apiFetch(`/api/dolluzcorp/blog/posts/${selectedId}`, { method: "DELETE", credentials: "include" });
        if (!res.ok) { dnAlert.error("Delete failed"); return; }
        setSelectedId(null);
        setEditor(null);
        await fetchAll();
    };

    return (
        <div className="dnews-blog-workspace">
            <section className={`dnews-blog-editor ${editor ? "" : "is-blank"}`}>
                {!editor ? (
                    <div className="dnews-tpl-blank">
                        <FaNewspaper className="dnews-tpl-blank-icon" />
                        <h3>Start a new post</h3>
                        <p>Or pick one from <strong>All Posts</strong> below to preview or edit it.</p>
                        {/* The action lives here as well as at the foot of the list -
                             on an empty page the list button sits below the fold. */}
                        <button className="dnews-el-primary-btn dnews-blog-blank-new" onClick={openNew}>
                            <FaPlus /> New post
                        </button>
                    </div>
                ) : (
                    <>
                        <div className="dnews-blog-editor-row">
                            <input
                                className="dnews-blog-title-input"
                                value={editor.title}
                                onChange={(e) => setEditor(prev => ({ ...prev, title: e.target.value }))}
                                placeholder="Post title…"
                                maxLength={250}
                            />
                            {editor.status && STATUS_PILL[editor.status] && (
                                <span className={STATUS_PILL[editor.status].className} style={{ marginLeft: 12 }}>
                                    {STATUS_PILL[editor.status].label}
                                </span>
                            )}
                        </div>

                        <div className="dnews-row" style={{ maxWidth: 380 }}>
                            <label className="dnews-label dnews-label-req">Category</label>
                            <DNewsSelect
                                value={editor.category_id || ""}
                                onChange={(v) => setEditor(prev => ({ ...prev, category_id: Number(v) }))}
                                options={categories.map(c => ({ value: c.id, label: c.name }))}
                                placeholder="Pick a category"
                            />
                        </div>

                        <div className="dnews-row" style={{ maxWidth: 380 }}>
                            <label className="dnews-label dnews-label-req">Who sees this?</label>
                            <Select
                                classNamePrefix="dnews-blog-aud"
                                {...SELECT_PORTAL}
                                options={AUDIENCE_TYPES}
                                value={AUDIENCE_TYPES.find(a => a.value === (editor.audience_type || "general"))}
                                onChange={(sel) => setEditor(prev => ({ ...prev, audience_type: sel.value, audience_value: [] }))}
                            />
                            {SCOPED.includes(editor.audience_type) && (
                                <div style={{ marginTop: 8 }}>
                                    <label className="dnews-label">Select {editor.audience_type.replace("_", " ")}</label>
                                    <Select
                                        key={editor.audience_type}
                                        isMulti
                                        {...checkboxMultiProps}
                                        classNamePrefix="dnews-blog-aud"
                                        {...SELECT_PORTAL}
                                        options={audienceOptionsFor(editor.audience_type)}
                                        value={audienceOptionsFor(editor.audience_type).filter(o => (editor.audience_value || []).includes(String(o.value)))}
                                        onChange={(sel) => setEditor(prev => ({ ...prev, audience_value: (sel || []).map(x => String(x.value)) }))}
                                    />
                                </div>
                            )}
                        </div>

                        {/* Content blocks */}
                        <div className="dnews-blog-blocks">
                            {editor.blocks.length === 0 && (
                                <div className="dnews-blog-empty-blocks">
                                    No content yet. Add a block below to start.
                                </div>
                            )}
                            {editor.blocks.map((b, i) => (
                                <BlockCard
                                    key={b.id}
                                    block={b}
                                    isFirst={i === 0}
                                    isLast={i === editor.blocks.length - 1}
                                    onChange={(patch) => updateBlock(b.id, patch)}
                                    onRemove={() => removeBlock(b.id)}
                                    onMoveUp={() => moveBlock(b.id, "up")}
                                    onMoveDown={() => moveBlock(b.id, "down")}
                                />
                            ))}
                        </div>

                        {/* Add-block toolbar */}
                        <div className="dnews-blog-add-toolbar">
                            <span className="dnews-blog-add-label">Add block:</span>
                            {BLOCK_TYPES.map(bt => (
                                <button key={bt.id} className="dnews-blog-add-btn" onClick={() => addBlock(bt.id)}>
                                    <bt.icon /> {bt.label}
                                </button>
                            ))}
                        </div>

                        {/* Publish panel */}
                        <div className="dnews-blog-publish-panel">
                            <div className="dnews-blog-publish-row">
                                {/* Leaves edit mode and clears the composer without a reload,
                                    matching the Cancel on every other Inside D page. */}
                                <button className="dnews-blog-cancel-btn" onClick={closeEditor} disabled={busy}>
                                    <FaTimes /> Cancel
                                </button>
                                <button className="dnews-el-secondary-btn" onClick={onSaveDraft} disabled={busy}>
                                    <FaSave /> Save draft
                                </button>
                                <button className="dnews-submit-btn" onClick={onPublishNow} disabled={busy}>
                                    <FaGlobe /> Publish now
                                </button>
                            </div>
                            <div className="dnews-blog-schedule">
                                <FaClock className="dnews-blog-schedule-icon" />
                                <input
                                    type="datetime-local"
                                    className="dnews-input dnews-blog-schedule-input"
                                    value={scheduleAt}
                                    onChange={(e) => setScheduleAt(e.target.value)}
                                />
                                <button className="dnews-el-secondary-btn" onClick={onSchedule} disabled={busy || !scheduleAt}>
                                    Schedule
                                </button>
                            </div>

                            {selectedId !== "new" && (
                                <div className="dnews-blog-dangerzone">
                                    {editor.status !== "draft" && (
                                        <button className="dnews-el-secondary-btn" onClick={onRevertToDraft}>
                                            <FaFileAlt /> Revert to draft
                                        </button>
                                    )}
                                    <button className="dnews-el-icon-btn danger" onClick={onDelete} title="Delete post">
                                        <FaTrashAlt /> Delete
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </section>

            {/* ── Bottom: every post, same shape as the other Inside D pages ── */}
            <section className="dnews-blog-published">
                <div className="dnews-blog-published-head">
                    <h4>All Posts <span className="dnews-blog-count">{posts.length}</span></h4>
                    <button className="dnews-el-primary-btn" onClick={openNew}><FaPlus /> New post</button>
                </div>
                {loadingList && <div className="dnews-el-empty">Loading…</div>}
                {!loadingList && posts.length === 0 && categories.length === 0 && (
                    <div className="dnews-el-empty">
                        <div style={{ marginBottom: 8 }}>No categories yet.</div>
                        <button className="dnews-el-primary-btn" onClick={() => setSubview && setSubview("categories")} style={{ fontSize: 12 }}>
                            Create your first category →
                        </button>
                    </div>
                )}
                {!loadingList && posts.length === 0 && categories.length > 0 && (
                    <div className="dnews-el-empty">No posts yet. Click <strong>+ New post</strong>.</div>
                )}
                <div className="dnews-blog-post-grid">
                    {posts.map(p => (
                        <div
                            key={p.id}
                            className={`dnews-blog-post-card ${selectedId === p.id ? "active" : ""}`}
                        >
                            {/* The card body opens the preview; the icons below do the
                                three actions. A <button> cannot nest buttons, so the
                                clickable area and the actions are siblings. */}
                            <button className="dnews-blog-post-main" onClick={() => openPreview(p.id)} title="Preview this post">
                                <div className="dnews-blog-post-thumb">
                                    {p.thumb
                                        ? <img src={mediaUrl(p.thumb)} alt="" />
                                        : <span className="dnews-blog-post-thumb-empty"><FaImage /></span>}
                                    <span className={`dnews-blog-post-pill ${p.status}`}>
                                        {STATUS_PILL[p.status]?.label || p.status}
                                    </span>
                                    {p.category_name && <span className="dnews-blog-post-cat">{p.category_name}</span>}
                                    <span className="dnews-blog-post-aud">{audienceBadge(p.audience_type, p.audience_value)}</span>
                                </div>
                                <div className="dnews-blog-post-body">
                                    <div className="dnews-blog-post-card-title">{p.title}</div>
                                    {p.excerpt && <p className="dnews-blog-post-excerpt">{p.excerpt}</p>}
                                </div>
                            </button>

                            <div className="dnews-blog-post-foot">
                                <span className="dnews-blog-post-card-time">
                                    {p.published_at
                                        ? new Date(p.published_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
                                        : p.scheduled_at
                                            ? `Scheduled ${new Date(p.scheduled_at).toLocaleDateString()}`
                                            : `Edited ${new Date(p.updated_at).toLocaleDateString()}`}
                                </span>
                                <div className="dnews-blog-post-actions">
                                    <button className="dnews-blog-post-icon" title="Preview" onClick={() => openPreview(p.id)}>
                                        <FaEye />
                                    </button>
                                    <button className="dnews-blog-post-icon" title="Edit" onClick={() => openPost(p.id)}>
                                        <FaPenSquare />
                                    </button>
                                    <button className="dnews-blog-post-icon danger" title="Delete" onClick={() => deletePost(p)}>
                                        <FaTrashAlt />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            {/* ── Reader preview ───────────────────────────────────────────
                 Opens on card click and renders the post with the SAME
                 RenderBlock the published site uses, so what is shown here is
                 what readers get. Editing is a deliberate step from inside. */}
            {preview && (
                <div className="dnews-blog-modal-backdrop" onClick={() => setPreview(null)}>
                    <div className="dnews-blog-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="dnews-blog-modal-bar">
                            <span className="dnews-blog-modal-label">Preview</span>
                            <div className="dnews-blog-modal-actions">
                                <button className="dnews-el-secondary-btn" onClick={editFromPreview}>
                                    <FaPenSquare /> Edit post
                                </button>
                                <button className="dnews-blog-modal-close" onClick={() => setPreview(null)} title="Close">
                                    <FaTimes />
                                </button>
                            </div>
                        </div>

                        {previewLoading || !preview.title ? (
                            <div className="dnews-blog-live-empty">Loading…</div>
                        ) : (
                            <article className="dnews-blog-modal-body">
                                {preview.category_name && (
                                    <div className="dnews-blog-live-cat"><FaTag /> {preview.category_name}</div>
                                )}
                                <h1 className="dnews-blog-live-title">{preview.title}</h1>
                                <div className="dnews-blog-modal-meta">
                                    <span className={STATUS_PILL[preview.status]?.className}>
                                        {STATUS_PILL[preview.status]?.label || preview.status}
                                    </span>
                                    {preview.published_at && <span>{new Date(preview.published_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>}
                                </div>
                                <div className="dnews-blog-live-body">
                                    {(preview.blocks || []).map((b, i) => <RenderBlock key={b?.id || i} block={b} />)}
                                </div>
                            </article>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// BLOCK CARD - one block in the editor
// ═══════════════════════════════════════════════════════════════════════════
function BlockCard({ block, isFirst, isLast, onChange, onRemove, onMoveUp, onMoveDown }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const colsRef = useRef(block.cols || [null, null]);
    useEffect(() => { colsRef.current = block.cols || [null, null]; }, [block.cols]);

    const onPickMedia = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) {
            dnAlert.error("File too large", `"${f.name}" exceeds the 5MB per-file limit.`);
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        // Don't upload yet - keep the File on the block and preview via a
        // blob URL. The actual upload runs on Save / Publish (see
        // uploadPendingMedia in ComposeView). This means a reload before
        // save = file gone = no orphan junk on the server.
        const blobUrl = URL.createObjectURL(f);
        onChange({ src: blobUrl, _pendingFile: f });
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <div className="dnews-blog-block">
            <div className="dnews-blog-block-header">
                <span className="dnews-blog-block-type">
                    {BLOCK_TYPES.find(t => t.id === block.type)?.label || block.type}
                </span>
                <div className="dnews-blog-block-tools">
                    {(block.type === "image" || block.type === "video") && (
                        <div className="dnews-blog-align" title="Position">
                            {/* The 'full' data value is kept for backward
                                compatibility but shown as "Center" - that's
                                what it actually does when size < 100%. */}
                            {[
                                { v: "left", l: "Left" },
                                { v: "full", l: "Center" },
                                { v: "right", l: "Right" },
                            ].map(a => (
                                <button
                                    key={a.v}
                                    className={`dnews-blog-align-btn ${block.align === a.v ? "active" : ""}`}
                                    onClick={() => onChange({ align: a.v })}
                                    title={`Align ${a.l.toLowerCase()}`}
                                >{a.l}</button>
                            ))}
                        </div>
                    )}
                    <button className="dnews-blog-block-icon" onClick={onMoveUp} disabled={isFirst} title="Move up"><FaArrowUp /></button>
                    <button className="dnews-blog-block-icon" onClick={onMoveDown} disabled={isLast} title="Move down"><FaArrowDown /></button>
                    <button className="dnews-blog-block-icon danger" onClick={onRemove} title="Remove"><FaTrashAlt /></button>
                </div>
            </div>

            {block.type === "heading" && (
                <HeadingBlockEditor block={block} onChange={onChange} />
            )}

            {block.type === "text" && (
                <TextBlockEditor block={block} onChange={onChange} />
            )}

            {block.type === "image" && (
                <div className="dnews-blog-media-shell">
                    <div className={`dnews-blog-media-block align-${block.align || "full"}`} style={block.src ? mediaSizeStyle(block) : undefined}>
                    {block.src ? (
                        <img
                            src={mediaUrl(block.src)}
                            alt={block.alt || ""}
                            className="dnews-blog-media-img"
                        />
                    ) : (
                        <button className="dnews-blog-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                            <FaUpload /> {uploading ? "Uploading…" : "Upload image"}
                        </button>
                    )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickMedia} hidden />
                    {block.src && (
                        <div className="dnews-blog-media-controls">
                            {/* Percentage of the column. Height follows automatically,
                                so the media scales proportionally and never distorts.
                                Saved on the block, so the portal renders it identically. */}
                            <div className="dnews-blog-imgsize">
                                <span className="dnews-blog-imgsize-label">Size: <strong>{mediaWidthPct(block)}%</strong></span>
                                <input
                                    type="range" min={MEDIA_PCT_MIN} max="100" step="5"
                                    value={mediaWidthPct(block)}
                                    onChange={(e) => onChange({ widthPct: Number(e.target.value) })}
                                />
                                <button
                                    className="dnews-blog-imgsize-reset"
                                    onClick={() => onChange({ widthPct: 100 })}
                                    disabled={mediaWidthPct(block) === 100}
                                >Reset</button>
                            </div>
                            <button className="dnews-blog-replace-btn" onClick={() => { if (block.src && block.src.startsWith("blob:")) URL.revokeObjectURL(block.src); onChange({ src: "", _pendingFile: null, widthPct: 100 }); }}>
                                <FaTimes /> Replace
                            </button>
                        </div>
                    )}
                </div>
            )}

            {block.type === "video" && (
                <div className="dnews-blog-media-shell">
                    <div className={`dnews-blog-media-block align-${block.align || "full"}`} style={block.src ? mediaSizeStyle(block) : undefined}>
                    {block.src ? (
                        <video
                            src={mediaUrl(block.src)}
                            controls
                            className="dnews-blog-media-video"
                        />
                    ) : (
                        <button className="dnews-blog-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                            <FaUpload /> {uploading ? "Uploading…" : "Upload video"}
                        </button>
                    )}
                    </div>
                    <input ref={fileRef} type="file" accept="video/*" onChange={onPickMedia} hidden />
                    {block.src && (
                        <div className="dnews-blog-media-controls">
                            <div className="dnews-blog-imgsize">
                                <span className="dnews-blog-imgsize-label">Size: <strong>{mediaWidthPct(block)}%</strong></span>
                                <input
                                    type="range" min={MEDIA_PCT_MIN} max="100" step="5"
                                    value={mediaWidthPct(block)}
                                    onChange={(e) => onChange({ widthPct: Number(e.target.value) })}
                                />
                                <button
                                    className="dnews-blog-imgsize-reset"
                                    onClick={() => onChange({ widthPct: 100 })}
                                    disabled={mediaWidthPct(block) === 100}
                                >Reset</button>
                            </div>
                            <button className="dnews-blog-replace-btn" onClick={() => { if (block.src && block.src.startsWith("blob:")) URL.revokeObjectURL(block.src); onChange({ src: "", _pendingFile: null, widthPct: 100 }); }}>
                                <FaTimes /> Replace
                            </button>
                        </div>
                    )}
                </div>
            )}

            {block.type === "divider" && <hr className="dnews-blog-divider" />}

            {/* Row block: two side-by-side slots, each can hold one leaf
                block. Cell content is rendered by InnerBlockEditor below. */}
            {block.type === "row" && (
                <div className="dnews-blog-row">
                    {[0, 1].map(i => (
                        <RowSlot
                            key={i}
                            inner={block.cols?.[i] || null}
                            onChangeInner={(patch) => {
                                const next = [...colsRef.current];
                                next[i] = patch === null
                                    ? null
                                    : { ...(next[i] || {}), ...patch };
                                colsRef.current = next;
                                onChange({ cols: next });
                            }}
                            onPickType={(type) => {
                                const newInner = { id: newBlockId(), type };
                                if (type === "heading" || type === "text") newInner.html = "";
                                if (type === "image" || type === "video") { newInner.src = ""; newInner.align = "full"; newInner.widthPct = 100; }
                                if (type === "image") newInner.alt = "";
                                const next = [...colsRef.current];
                                next[i] = newInner;
                                colsRef.current = next;
                                onChange({ cols: next });
                            }}
                            onClear={() => {
                                const next = [...colsRef.current];
                                next[i] = null;
                                colsRef.current = next;
                                onChange({ cols: next });
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── RowSlot: one column inside a Row block. Empty → picker; filled →
//    InnerBlockEditor + small clear button. ─────────────────────────────────
function RowSlot({ inner, onChangeInner, onPickType, onClear }) {
    if (!inner) {
        return (
            <div className="dnews-blog-row-slot empty">
                <div className="dnews-blog-row-slot-label">Pick a block:</div>
                {LEAF_TYPES.map(t => (
                    <button
                        key={t.id}
                        className="dnews-blog-add-btn"
                        onClick={() => onPickType(t.id)}
                    >
                        <t.icon /> {t.label}
                    </button>
                ))}
            </div>
        );
    }
    return (
        <div className="dnews-blog-row-slot filled">
            <div className="dnews-blog-row-slot-header">
                <span className="dnews-blog-block-type">
                    {LEAF_TYPES.find(t => t.id === inner.type)?.label || inner.type}
                </span>
                <button className="dnews-blog-block-icon danger" onClick={onClear} title="Clear slot">
                    <FaTimes />
                </button>
            </div>
            <InnerBlockEditor inner={inner} onChange={onChangeInner} />
        </div>
    );
}

// ── InnerBlockEditor: renders one leaf block inside a Row slot. Reuses the
//    same Quill / upload helpers BlockCard uses, but without the outer
//    header / move buttons (the row-slot header handles clear + type label).
function InnerBlockEditor({ inner, onChange }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const onPickMedia = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 5 * 1024 * 1024) {
            dnAlert.error("File too large", `"${f.name}" exceeds the 5MB per-file limit.`);
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        // Same defer-until-save pattern as standalone media (see comment
        // on the standalone BlockCard.onPickMedia).
        const blobUrl = URL.createObjectURL(f);
        onChange({ src: blobUrl, _pendingFile: f });
        if (fileRef.current) fileRef.current.value = "";
    };

    if (inner.type === "heading") return <HeadingBlockEditor block={inner} onChange={onChange} />;
    if (inner.type === "text") return <TextBlockEditor block={inner} onChange={onChange} />;

    if (inner.type === "image") {
        return (
            <div className="dnews-blog-media-shell">
                <div className={`dnews-blog-media-block align-full`} style={inner.src ? mediaSizeStyle(inner) : undefined}>
                {inner.src ? (
                    <img
                        src={mediaUrl(inner.src)}
                        alt={inner.alt || ""}
                        className="dnews-blog-media-img"
                    />
                ) : (
                    <button className="dnews-blog-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                        <FaUpload /> {uploading ? "Uploading…" : "Upload image"}
                    </button>
                )}
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={onPickMedia} hidden />
                {inner.src && (
                    <div className="dnews-blog-media-controls">
                        <div className="dnews-blog-imgsize">
                            <span className="dnews-blog-imgsize-label">Size: <strong>{mediaWidthPct(inner)}%</strong></span>
                            <input type="range" min={MEDIA_PCT_MIN} max="100" step="5" value={mediaWidthPct(inner)} onChange={(e) => onChange({ widthPct: Number(e.target.value) })} />
                            <button className="dnews-blog-imgsize-reset" onClick={() => onChange({ widthPct: 100 })} disabled={mediaWidthPct(inner) === 100}>Reset</button>
                        </div>
                        <button className="dnews-blog-replace-btn" onClick={() => { if (inner.src && inner.src.startsWith("blob:")) URL.revokeObjectURL(inner.src); onChange({ src: "", _pendingFile: null, widthPct: 100 }); }}>
                            <FaTimes /> Replace
                        </button>
                    </div>
                )}
            </div>
        );
    }

    if (inner.type === "video") {
        return (
            <div className="dnews-blog-media-shell">
                <div className={`dnews-blog-media-block align-full`} style={inner.src ? mediaSizeStyle(inner) : undefined}>
                {inner.src ? (
                    <video
                        src={mediaUrl(inner.src)}
                        controls
                        className="dnews-blog-media-video"
                    />
                ) : (
                    <button className="dnews-blog-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
                        <FaUpload /> {uploading ? "Uploading…" : "Upload video"}
                    </button>
                )}
                </div>
                <input ref={fileRef} type="file" accept="video/*" onChange={onPickMedia} hidden />
                {inner.src && (
                    <div className="dnews-blog-media-controls">
                        <div className="dnews-blog-imgsize">
                            <span className="dnews-blog-imgsize-label">Size: <strong>{mediaWidthPct(inner)}%</strong></span>
                            <input type="range" min={MEDIA_PCT_MIN} max="100" step="5" value={mediaWidthPct(inner)} onChange={(e) => onChange({ widthPct: Number(e.target.value) })} />
                            <button className="dnews-blog-imgsize-reset" onClick={() => onChange({ widthPct: 100 })} disabled={mediaWidthPct(inner) === 100}>Reset</button>
                        </div>
                        <button className="dnews-blog-replace-btn" onClick={() => { if (inner.src && inner.src.startsWith("blob:")) URL.revokeObjectURL(inner.src); onChange({ src: "", _pendingFile: null, widthPct: 100 }); }}>
                            <FaTimes /> Replace
                        </button>
                    </div>
                )}
            </div>
        );
    }
    return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// PER-BLOCK QUILL EDITORS
// ═══════════════════════════════════════════════════════════════════════════
// Each heading + paragraph block owns its own Quill instance. Quill is
// initialised once per mount; we sync `block.html` → parent state via the
// "text-change" listener. Initial content is pasted via dangerouslyPasteHTML
// ONLY on first mount so re-renders from parent state don't fight with
// in-editor cursor position.

// Cleanup helper for Quill - removes the toolbar element Quill appends as
// a SIBLING of the ref'd div. Without this, React 18 strict-mode (and
// re-mounts in general) leave the previous toolbar in the DOM, so you see
// two stacked toolbars. Called from each editor's useEffect cleanup.
function cleanupQuillToolbar(wrapperEl) {
    if (!wrapperEl) return;
    const toolbars = wrapperEl.querySelectorAll(".ql-toolbar");
    // Keep only the LAST toolbar (the live one); strip any older siblings.
    for (let i = 0; i < toolbars.length - 1; i++) {
        toolbars[i].remove();
    }
}

function HeadingBlockEditor({ block, onChange }) {
    const { quill, quillRef } = useQuill({
        theme: "snow",
        placeholder: "Heading text…",
        modules: {
            toolbar: [
                // ONE size dropdown - Small / Normal / Large / Huge.
                [{ size: ["small", false, "large", "huge"] }],
                ["bold", "italic", "underline"],
                [{ align: [] }],
                [{ color: [] }],
                ["clean"],
            ],
        },
    });
    const wrapperRef = useRef(null);
    const initialisedRef = useRef(false);

    useEffect(() => {
        if (!quill) return;
        // Wrapper is the DOM parent of Quill's toolbar AND editor - sweep
        // any stale toolbar that survived a remount (React 18 strict mode).
        cleanupQuillToolbar(wrapperRef.current);
        if (!initialisedRef.current) {
            const initial = block.html != null
                ? block.html
                : (block.text ? `<p>${block.text}</p>` : "");
            if (initial) quill.clipboard.dangerouslyPasteHTML(initial);
            initialisedRef.current = true;
        }
        const handler = () => onChange({ html: quill.root.innerHTML });
        quill.on("text-change", handler);
        return () => quill.off("text-change", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quill]);

    return (
        <div ref={wrapperRef}>
            <div ref={quillRef} className="dnews-blog-heading-quill" />
        </div>
    );
}

function TextBlockEditor({ block, onChange }) {
    const { quill, quillRef } = useQuill({
        theme: "snow",
        placeholder: "Paragraph text…",
        modules: {
            toolbar: [
                [{ size: ["small", false, "large", "huge"] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                [{ align: [] }],
                [{ color: [] }, { background: [] }],
                ["link"],
                ["clean"],
            ],
        },
    });
    const wrapperRef = useRef(null);
    const initialisedRef = useRef(false);

    useEffect(() => {
        if (!quill) return;
        cleanupQuillToolbar(wrapperRef.current);
        if (!initialisedRef.current) {
            const initial = block.html != null
                ? block.html
                : (block.text ? `<p>${block.text}</p>` : "");
            if (initial) quill.clipboard.dangerouslyPasteHTML(initial);
            initialisedRef.current = true;
        }
        const handler = () => onChange({ html: quill.root.innerHTML });
        quill.on("text-change", handler);
        return () => quill.off("text-change", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quill]);

    return (
        <div ref={wrapperRef}>
            <div ref={quillRef} className="dnews-blog-text-quill" />
        </div>
    );
}

// ═══════════════════════════════════════════════════════════════════════════
// CATEGORIES VIEW
// ═══════════════════════════════════════════════════════════════════════════
function CategoriesView({ navigate }) {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);    // null | {} (new) | {existing}

    const fetchList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/api/dolluzcorp/blog/categories/list", { credentials: "include" });
            if (res.status === 401 || res.status === 403) { navigate("/login"); return; }
            setCategories(await res.json());
        } finally { setLoading(false); }
    }, [navigate]);

    useEffect(() => { fetchList(); }, [fetchList]);

    const onDelete = async (c) => {
        if (c.post_count > 0) {
            dnAlert.warning(
                `Can't delete "${c.name}"`,
                `${c.post_count} post${c.post_count === 1 ? "" : "s"} still use this category. Re-categorise them first.`
            );
            return;
        }
        const ok = await dnAlert.confirm({
            title: `Delete "${c.name}"?`,
            message: "The category and its thumbnail file will be removed.",
            confirmLabel: "Delete",
            danger: true,
        });
        if (!ok) return;
        const res = await apiFetch(`/api/dolluzcorp/blog/categories/${c.id}`, { method: "DELETE", credentials: "include" });
        const data = await res.json();
        if (!res.ok) { dnAlert.error("Failed", data.error); return; }
        await fetchList();
    };

    // ── Move category up/down by swapping sort_order with neighbour ────
    const moveCategory = async (idx, dir) => {
        const swapIdx = dir === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= categories.length) return;
        const next = [...categories];
        [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
        // Optimistic update so the move feels instant; server confirms.
        setCategories(next);
        await apiFetch("/api/dolluzcorp/blog/categories/reorder", {
            method: "POST", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ order: next.map(c => c.id) }),
        });
        fetchList();
    };

    return (
        <>
            <div className="dnews-el-toolbar">
                <div className="dnews-el-count" style={{ flex: 1 }}>
                    {loading ? "Loading…" : `${categories.length} categor${categories.length === 1 ? "y" : "ies"} · used by the public site grid`}
                </div>
                <button className="dnews-el-primary-btn" onClick={() => setModal({})}>
                    <FaPlus /> New category
                </button>
            </div>

            <div className="dnews-blog-cat-grid">
                {!loading && categories.length === 0 && (
                    <div className="dnews-el-empty" style={{ gridColumn: "1/-1", padding: 40 }}>
                        No categories yet. Click <strong>+ New category</strong> to create one.
                    </div>
                )}
                {categories.map((c, i) => (
                    <div key={c.id} className="dnews-blog-cat-card">
                        {c.thumbnail_path && (
                            <img
                                src={`${API_BASE}/api/dolluzcorp/blog/categories/thumb/${c.thumbnail_path.split("/").pop()}`}
                                alt={c.name}
                                className="dnews-blog-cat-thumb"
                            />
                        )}
                        <div className="dnews-blog-cat-body">
                            <h4>{c.name}</h4>
                            <p>{c.description}</p>
                            <div className="dnews-blog-cat-meta">
                                <span className="dnews-el-type-pill client">/{c.slug}</span>
                                <span>{c.post_count} post{c.post_count === 1 ? "" : "s"}</span>
                            </div>
                            <div className="dnews-blog-cat-actions">
                                <button className="dnews-el-icon-btn" disabled={i === 0} onClick={() => moveCategory(i, "up")} title="Move up"><FaArrowUp /></button>
                                <button className="dnews-el-icon-btn" disabled={i === categories.length - 1} onClick={() => moveCategory(i, "down")} title="Move down"><FaArrowDown /></button>
                                <button className="dnews-el-secondary-btn" onClick={() => setModal(c)}>
                                    <FaPenSquare /> Edit
                                </button>
                                <button className="dnews-el-icon-btn danger" onClick={() => onDelete(c)}>
                                    <FaTrashAlt />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {modal && <CategoryModal initial={modal} onClose={() => setModal(null)} onSaved={fetchList} />}
        </>
    );
}

function CategoryModal({ initial, onClose, onSaved }) {
    const isEdit = !!initial.id;
    const [name, setName] = useState(initial.name || "");
    const [description, setDescription] = useState(initial.description || "");
    const [thumb, setThumb] = useState(null);    // File or null
    const [busy, setBusy] = useState(false);

    // Live preview of a freshly picked file (before it's uploaded). The object
    // URL must be revoked when the file changes / modal closes to avoid a leak.
    const [thumbPreview, setThumbPreview] = useState(null);
    useEffect(() => {
        if (!thumb) { setThumbPreview(null); return; }
        const url = URL.createObjectURL(thumb);
        setThumbPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [thumb]);

    // Auto-slug preview from the name (server enforces uniqueness, this is
    // just visual feedback so admins know what the URL will be).
    const slugPreview = String(name || "")
        .toLowerCase().replace(/[^a-z0-9\s]/g, "").trim().replace(/\s+/g, "-").slice(0, 80);

    const onSave = async () => {
        if (!name.trim()) { dnAlert.warning("Name is required"); return; }
        if (!description.trim()) { dnAlert.warning("Description is required"); return; }
        if (!isEdit && !thumb) { dnAlert.warning("Thumbnail is required"); return; }

        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("name", name.trim());
            fd.append("description", description.trim());
            if (thumb) fd.append("thumbnail", thumb);
            const url = isEdit ? `/api/dolluzcorp/blog/categories/${initial.id}` : "/api/dolluzcorp/blog/categories/create";
            const res = await apiFetch(url, {
                method: isEdit ? "PUT" : "POST",
                credentials: "include",
                body: fd,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            dnAlert.success(isEdit ? "Updated" : "Created");
            onSaved();
            onClose();
        } catch (e) {
            dnAlert.error("Save failed", e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="dnews-el-modal-backdrop">
            <div className="dnews-el-modal" onClick={e => e.stopPropagation()}>
                <div className="dnews-el-modal-header">
                    <h3>{isEdit ? "Edit Category" : "New Category"}</h3>
                    <button onClick={onClose}><FaTimes /></button>
                </div>
                <div className="dnews-el-modal-body">
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Category Name</label>
                        <input className="dnews-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Artificial Intelligence" maxLength={120} />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label">URL Slug (auto-generated)</label>
                        <input className="dnews-input" readOnly value={slugPreview || "-"} style={{ background: "#f8fafc", color: "#94a3b8" }} />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">One-line Description</label>
                        <input className="dnews-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Shown under the category title on the public site" maxLength={250} />
                    </div>
                    <div className="dnews-row">
                        <label className="dnews-label dnews-label-req">Thumbnail (16:9 recommended)</label>
                        {isEdit && initial.thumbnail_path && !thumb && (
                            <img
                                src={`${API_BASE}/api/dolluzcorp/blog/categories/thumb/${initial.thumbnail_path.split("/").pop()}`}
                                alt=""
                                className="dnews-blog-thumb-preview"
                            />
                        )}
                        {thumb && (
                            <div style={{ marginBottom: 4 }}>
                                <img
                                    src={thumbPreview}
                                    alt=""
                                    className="dnews-blog-thumb-preview"
                                />
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>{thumb.name}</div>
                            </div>
                        )}
                        <label className="dnews-el-secondary-btn" style={{ display: "inline-flex" }}>
                            <FaUpload /> {isEdit ? "Replace thumbnail" : "Choose thumbnail"}
                            <input type="file" accept="image/*" onChange={(e) => setThumb(e.target.files?.[0] || null)} hidden />
                        </label>
                    </div>
                </div>
                <div className="dnews-el-modal-footer">
                    <button className="dnews-el-secondary-btn" onClick={onClose} disabled={busy}>Cancel</button>
                    <button className="dnews-submit-btn" onClick={onSave} disabled={busy}>
                        {busy
                            ? <><span className="dn-spinner" /> Saving…</>
                            : (isEdit ? "Save changes" : "Create category")}
                    </button>
                </div>
            </div>

        </div>
    );
}
